/**
 * Calendar module: create/edit modal handling.
 * The month grid opens the same modal in two modes — a day cell prefills a
 * new event, an event chip loads the existing one (shared targets may edit;
 * only the owner sees the delete button).
 */
import { attachDatePicker, closeDatePicker, isDatePickerOpen, syncDateLabel } from './datepicker.js';
import { api, history, createCommand, deleteCommand, updateCommand, patchCommand, record, commit, refreshOrReload, showToast, dismissToast, labels } from './calendar-history.js';
import { initInappSelects, syncSelect } from './inapp-select.js';
import { appConfirm, appChoose } from './confirm-dialog.js';
import { bindPropertyCreate } from './calendar-property-create.js';
import { attachTimeInput } from './time-input.js';
import { bindRepeatControl } from './calendar-repeat.js';
import { parseQuick, quickChipLabel } from './quick-time.js';
import { openPillPicker, pill } from './prop-picker.js';
import { openPeoplePicker } from './people-picker.js';
import { openEventPicker } from './event-picker.js';
import { isPhone } from './phone.js';
import { fileTile, uploadFiles, bindDrop, CAL_MAX_FILES } from './calendar-files.js';

// 공유 대상 칩/행에 쓰는 lucide 아이콘 (user / building-2 / users)
const CAL_ICONS = {
    users: '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>',
    depts: '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18"></path><path d="M2 22h20"></path><path d="M10 7h1"></path><path d="M13 7h1"></path><path d="M10 11h1"></path><path d="M13 11h1"></path><path d="M10 15h1"></path><path d="M13 15h1"></path></svg>',
    groups: '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>',
};

// 로컬 기준 YYYY-MM-DD. toISOString 은 UTC 라 KST 에서 하루 밀린다 — 금지.
function localISODate(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// 목록에 없는 사람(그 사이 비활성화됐거나 시스템 사용자가 된 담당자·공유 대상)은
// ⚠ 표시 옵션 하나로 끼워 넣는다 — /admin 일괄 편집 select 와 같은 규칙. 일정 패널은
// 칩의 data-missing-people 을 넘긴다(Z3 — 예전엔 names 없이 불러 비활성 공유 대상이 저장 때
// 조용히 빠졌다). 서버도 같은 옵션을 그리므로 여기는 SPA 로 갈아끼운 옛 select 를 위한 보강이다.
function syncMissingOptions(selectEl, values, names) {
    if (!selectEl || !names) return;
    const wanted = new Set(values.filter(Boolean));
    Array.from(selectEl.querySelectorAll('option[data-missing-person]')).forEach((o) => {
        if (!wanted.has(o.value)) o.remove();
    });
    wanted.forEach((v) => {
        if (Array.from(selectEl.options).some((o) => o.value === v)) return;
        const o = document.createElement('option');
        o.value = v;
        o.textContent = '⚠ ' + (names[v] || v) + ' (퇴사/비활성)';
        o.dataset.missingPerson = '1';
        selectEl.appendChild(o);
    });
}

// 편집 버튼의 data-missing-people (id→이름 JSON) 을 읽는다.
function missingPeopleOf(ds) {
    try { return JSON.parse((ds && ds.missingPeople) || '{}') || {}; } catch (_) { return {}; }
}

function setMulti(selectEl, csv, names) {
    if (!selectEl) return;
    const wanted = new Set((csv || '').split(',').filter(Boolean));
    syncMissingOptions(selectEl, Array.from(wanted), names);
    Array.from(selectEl.options).forEach((o) => {
        o.selected = wanted.has(o.value);
    });
}

export function setupCalendar() {
    // 저장/삭제 결과 토스트 (레이아웃 이동 없음) + 쿼리 정리.
    const flash = document.getElementById('calendar-flash');
    if (flash && window.showNotification) {
        window.showNotification(flash.dataset.message, flash.dataset.error === '1' ? 'error' : 'success');
        const url = new URL(window.location.href);
        if (url.searchParams.has('flash')) {
            url.searchParams.delete('flash');
            window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
        }
    }

    const modal = document.getElementById('calendarModal');
    if (!modal) return;

    const form = document.getElementById('calendarForm');
    const titleEl = document.getElementById('calTitle');
    const startEl = document.getElementById('calStart');
    const endEl = document.getElementById('calEnd');
    const startBtn = document.getElementById('calStartBtn');
    const endBtn = document.getElementById('calEndBtn');
    const sTime = document.getElementById('calSTime');
    const eTime = document.getElementById('calETime');
    const saveState = document.getElementById('calSaveState');
    const modalTitle = document.getElementById('calendarModalTitle');
    const pickPh = modal.dataset.pickDate || '날짜 선택';

    const startTimeControl = attachTimeInput(sTime);
    const endTimeControl = attachTimeInput(eTime);
    initInappSelects(modal);
    function setTime(input, value, fallback) {
        input.value = value || fallback;
        input.setCustomValidity('');
        input.removeAttribute('aria-invalid');
    }

    // ── 날짜: 인앱 데이트피커 (표기 고정 YYYY-MM-DD) ──
    function syncDates() {
        // 반복 프리셋(매주 월요일 …)은 시작일을 따른다 — 컨트롤은 아래에서 만들어져 전역 손잡이로 부른다
        if (window.__calRepeatCtl) window.__calRepeatCtl.setDtstart(startEl.value);
        syncDateLabel(startBtn, startEl, pickPh);
        syncDateLabel(endBtn, endEl, pickPh);
    }
    // 프로젝트 작업은 시작일/종료일을 각각 비울 수 있다 (미정) — 픽커의 "미정" 버튼
    // 주 시작 요일 (XA21): 패널의 달력도 보는 사람의 설정을 따른다
    const dpBase = { weekdays: modal.dataset.weekdays, weekStart: modal.dataset.weekStart, placeholder: pickPh, clearLabel: modal.dataset.lTbd || '미정' };
    attachDatePicker(startBtn, startEl, Object.assign({}, dpBase, { onSet: (v) => {
        if (v && endEl.value && endEl.value < v) endEl.value = v;
        syncDates();
        markDirty();
    } }));
    attachDatePicker(endBtn, endEl, Object.assign({}, dpBase, { onSet: (v) => {
        if (v && startEl.value && v < startEl.value) startEl.value = v;
        syncDates();
        markDirty();
    } }));

    window.toggleCalendarTimes = function () {
        const allDay = document.getElementById('calAllDay').checked;
        document.getElementById('calTimes').style.display = allDay ? 'none' : 'inline-flex';
        [sTime, eTime].forEach((input) => { input.disabled = allDay; });
        startTimeControl.close();
        endTimeControl.close();
        // 세그먼트 표시 동기화 (edit 프리필 등 체크박스를 직접 만진 경로).
        const segA = document.getElementById('calSegAllday');
        const segT = document.getElementById('calSegTimed');
        if (segA && segT) {
            segA.classList.toggle('active', allDay);
            segT.classList.toggle('active', !allDay);
        }
    };

    // ── 공유 대상 피커 팩토리: 칩 + 탭/검색 패널 (숨은 multi-select 가
    // 폼 제출의 원본). 일정 모달과 프로젝트 캘린더 모달이 같이 쓴다. ──
    function makeSharePicker(ids, onChange) {
        const sel = {
            users: document.getElementById(ids.users),
            depts: document.getElementById(ids.depts),
            groups: document.getElementById(ids.groups),
        };
        const chipsBox = document.getElementById(ids.chips);
        const emptyLabel = document.getElementById(ids.empty);
        const panelEl = document.getElementById(ids.panel);
        const addBtn = document.getElementById(ids.add);
        const searchEl = document.getElementById(ids.search);
        const listEl = document.getElementById(ids.list);
        if (!chipsBox || !panelEl) return null;
        let tab = 'users';

        function renderChips() {
            chipsBox.querySelectorAll('.cal-share-chip').forEach((c) => c.remove());
            let any = false;
            Object.keys(sel).forEach((kind) => {
                Array.from(sel[kind].selectedOptions).forEach((o) => {
                    any = true;
                    const chip = document.createElement('span');
                    chip.className = 'cal-share-chip';
                    chip.innerHTML = CAL_ICONS[kind];
                    const label = document.createElement('span');
                    label.textContent = o.textContent.trim();
                    chip.appendChild(label);
                    const rm = document.createElement('button');
                    rm.type = 'button';
                    rm.textContent = '✕';
                    rm.setAttribute('aria-label', 'remove');
                    rm.addEventListener('click', () => {
                        o.selected = false;
                        renderChips();
                        renderList();
                        if (onChange) onChange();
                    });
                    chip.appendChild(rm);
                    chipsBox.appendChild(chip);
                });
            });
            emptyLabel.style.display = any ? 'none' : '';
        }

        function renderList() {
            listEl.innerHTML = '';
            const q = (searchEl.value || '').trim().toLowerCase();
            Array.from(sel[tab].options).forEach((o) => {
                const text = o.textContent.trim();
                if (q && !text.toLowerCase().includes(q)) return;
                const row = document.createElement('button');
                row.type = 'button';
                row.className = 'cal-share-row' + (o.selected ? ' sel' : '');
                const check = document.createElement('span');
                check.className = 'cal-share-check';
                check.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
                row.appendChild(check);
                const label = document.createElement('span');
                label.textContent = text;
                row.appendChild(label);
                row.addEventListener('click', () => {
                    o.selected = !o.selected;
                    row.classList.toggle('sel', o.selected);
                    renderChips();
                    if (onChange) onChange();
                });
                listEl.appendChild(row);
            });
        }

        addBtn.addEventListener('click', () => {
            const show = panelEl.hidden;
            panelEl.hidden = !show;
            addBtn.setAttribute('aria-expanded', show ? 'true' : 'false');
            if (show) {
                renderList();
                searchEl.focus();
            }
        });
        panelEl.querySelectorAll('[data-share-tab]').forEach((btn) => {
            btn.addEventListener('click', () => {
                tab = btn.dataset.shareTab;
                panelEl.querySelectorAll('[data-share-tab]').forEach((b) => b.classList.toggle('active', b === btn));
                renderList();
            });
        });
        searchEl.addEventListener('input', renderList);

        return {
            selects: sel,
            panel: panelEl,
            addBtn: addBtn,
            searchEl: searchEl,
            renderChips: renderChips,
            reset() {
                panelEl.hidden = true;
                addBtn.setAttribute('aria-expanded', 'false');
                searchEl.value = '';
                renderChips();
            },
        };
    }

    const eventPicker = makeSharePicker({
        users: 'calShareUsers', depts: 'calShareDepts', groups: 'calShareGroups',
        chips: 'calShareChips', empty: 'calShareEmpty', panel: 'calSharePanel',
        add: 'calShareAdd', search: 'calShareSearch', list: 'calShareList',
    }, () => markDirty());
    const selects = eventPicker.selects;
    const panel = eventPicker.panel;
    const shareAdd = eventPicker.addBtn;
    const shareSearch = eventPicker.searchEl;
    const shareAll = document.getElementById('calShareAll');
    const shareAllHint = document.getElementById('calShareAllHint');
    function renderChips() {
        eventPicker.renderChips();
        const isPublic = shareAll.value === '1';
        shareAllHint.hidden = !isPublic;
        document.getElementById('calShareChips').hidden = isPublic;
        shareAdd.hidden = isPublic;
        if (isPublic) {
            panel.hidden = true;
            shareAdd.setAttribute('aria-expanded', 'false');
        }
        syncSelect(shareAll);
    }
    shareAll.addEventListener('change', renderChips);

    // ── 저장 상태: 명시적으로 저장을 누르기 전까지는 서버에 쓰지 않는다 ──
    // dirty 면 저장 버튼이 켜지고, 닫을 때 확인을 받는다.
    let savedAny = false;
    let dirty = false;
    let mode = 'create'; // 'create' | 'read' | 'edit'
    const saveBtn = document.getElementById('calSaveBtn');
    // ── 반복 (반복 스펙 §6): 반복 행의 컨트롤 + 범위 확인 ──
    const repeatRow = document.getElementById('calRepeatRow');
    const repeat = bindRepeatControl(repeatRow);
    const RL = repeatRow ? repeatRow.dataset : {};
    window.__calRepeatCtl = repeat; // syncDates(위에 정의)가 시작일 변경을 알리는 손잡이
    const fmtN = (tpl, n) => String(tpl || '').replace(/%d/, String(n));
    // kind: 'edit' | 'delete' | 'rule' (규칙 변경은 이 일정만 을 주지 않는다)
    function askSeriesScope(kind) {
        const choices = [];
        if (kind !== 'rule') choices.push({ value: 'this', label: RL.scopeThis || '이 일정만', primary: true });
        choices.push({ value: 'following', label: RL.scopeFollowing || '이후 모두', danger: kind === 'delete', primary: kind === 'rule' });
        choices.push({ value: 'all', label: RL.scopeAll || '전체', danger: kind === 'delete' });
        const title = kind === 'delete' ? (RL.scopeDeleteTitle || '반복 일정을 어떻게 지울까요?')
            : kind === 'rule' ? (RL.scopeRuleTitle || '바뀐 규칙을 어디까지 적용할까요?')
                : (RL.scopeTitle || '반복 일정을 어떻게 바꿀까요?');
        return appChoose({ title, choices });
    }
    // ── 제목의 날짜·시간·반복 감지 (AZ4): 빠른 추가와 같은 parseQuick. 칩의 「적용」이 제목을 떼고
    // 시작·종료일·시각(종일 해제)·반복(새 일정만)을 칸에 채운다 — 저장은 하지 않는다. ✕ 는 제목을 비울
    // 때까지 끈다. 사용자가 치는 input 에서만 돈다(일정을 열 때 이미 있는 제목은 건드리지 않는다).
    const detectBox = document.getElementById('calTitleDetect');
    const detectLabel = document.getElementById('calTitleDetectLabel');
    let detectMuted = false;
    let detected = null;
    function paintTitleDetect() {
        if (!detectBox) return;
        if (!titleEl.value.trim()) detectMuted = false;
        // 반복은 새 일정에서만 읽는다 (이미 있는 일정의 규칙은 「규칙 바꾸기…」로)
        detected = detectMuted || mode === 'read' ? null : parseQuick(titleEl.value, {
            base: startEl.value || '', allowRepeat: mode === 'create' && !!repeat && !repeat.isLocked(),
            weekStart: detectBox.dataset.weekStart || modal.dataset.weekStart,
        });
        detectBox.hidden = !detected;
        if (detected && detectLabel) detectLabel.textContent = quickChipLabel(detected, { weekdays: detectBox.dataset.weekdays, fromFmt: detectBox.dataset.quickFrom });
    }
    function hideTitleDetect() { detected = null; detectMuted = false; if (detectBox) detectBox.hidden = true; }
    function applyTitleDetect() {
        const q = detected;
        if (!q) return;
        titleEl.value = q.title;
        if (q.date) {
            const s0 = startEl.value, e0 = endEl.value;
            const span = s0 && e0 && e0 >= s0 ? Math.round((new Date(e0 + 'T00:00:00') - new Date(s0 + 'T00:00:00')) / 86400000) : 0;
            startEl.value = q.date;
            const e = new Date(q.date + 'T00:00:00');
            e.setDate(e.getDate() + span);
            endEl.value = localISODate(e);
            syncDates();
        }
        if (q.start) {
            document.getElementById('calAllDay').checked = false;
            window.toggleCalendarTimes();
            setTime(sTime, q.start, '09:00');
            setTime(eTime, q.end, '10:00');
            sTime.dispatchEvent(new Event('change', { bubbles: true }));
            eTime.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (q.repeat && repeat && mode === 'create') repeat.setRule(q.repeat, q.date || startEl.value);
        hideTitleDetect();
        titleEl.dispatchEvent(new Event('input', { bubbles: true }));
        markDirty();
        titleEl.focus();
    }
    if (detectBox) {
        titleEl.addEventListener('input', paintTitleDetect);
        document.getElementById('calTitleDetectApply').addEventListener('click', applyTitleDetect);
        const off = document.getElementById('calTitleDetectOff');
        off.addEventListener('mousedown', (e) => e.preventDefault());
        off.addEventListener('click', () => { detectMuted = true; paintTitleDetect(); titleEl.focus(); });
    }
    // 인스턴스를 열 때 시리즈의 규칙을 받아 잠긴 요약을 그린다 (읽기 행·편집 행 둘 다).
    let seriesCache = {};
    function loadSeries(seriesID) {
        if (!seriesID) return Promise.resolve(null);
        if (seriesCache[seriesID]) return Promise.resolve(seriesCache[seriesID]);
        return fetch('/calendar/series/' + encodeURIComponent(seriesID), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => { if (j) seriesCache[seriesID] = j; return j; })
            .catch(() => null);
    }

    function setSaveState(kind) {
        if (!saveState) return;
        saveState.classList.toggle('err', kind === 'failed');
        if (!kind) {
            saveState.classList.remove('show');
            saveState.textContent = '';
            return;
        }
        saveState.textContent = saveState.dataset[kind] || '';
        saveState.classList.add('show');
    }
    function setDirty(v) {
        dirty = v;
        if (saveBtn) saveBtn.disabled = mode === 'edit' && !dirty;
        if (mode === 'edit') setSaveState(dirty ? 'dirty' : null);
    }
    function markDirty() {
        if (mode === 'read') return;
        setDirty(true);
    }
    // 마지막 저장 시각 표시 후 잠시 뒤 지움
    let saveTimer = null;
    function flashSaved() {
        setSaveState('saved');
        clearTimeout(saveTimer);
        saveTimer = setTimeout(() => { if (!dirty) setSaveState(null); }, 1800);
    }

    // ── 시간 정합성: 같은 날이면 종료 > 시작. 바꾼 쪽을 기준으로 다른 쪽을 밀어 준다 ──
    function timeToMin(t) {
        const m = /^(\d{2}):(\d{2})$/.exec(t || '');
        return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : NaN;
    }
    function minToTime(m) {
        m = Math.max(0, Math.min(23 * 60 + 59, m));
        return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
    }
    function normalizeTimes(changed) {
        if (document.getElementById('calAllDay').checked) return;
        if (startEl.value && endEl.value && startEl.value !== endEl.value) return; // 여러 날 일정은 시간 순서 무관
        const st = timeToMin(sTime.value), et = timeToMin(eTime.value);
        if (isNaN(st) || isNaN(et) || st < et) return;
        if (changed === 'end') setTime(sTime, minToTime(et - 60), '09:00');
        else setTime(eTime, minToTime(st + 60), '10:00');
    }
    sTime.addEventListener('change', () => normalizeTimes('start'));
    eTime.addEventListener('change', () => normalizeTimes('end'));

    form.addEventListener('input', markDirty);
    form.addEventListener('change', markDirty);
    // 종일/시간 토글 뒤에도 시간 정합성 확인
    document.getElementById('calAllDay').addEventListener('change', () => normalizeTimes('start'));

    // ── 실행 취소 스택: 모달에서 연 일정의 원본 스냅샷 (저장 한 번 = 히스토리 한 항목) ──
    let editBefore = null;
    let sessionCmd = null;
    function snapshotForm() {
        return new URLSearchParams(new FormData(form));
    }
    function paramsFromChip(chip) {
        const d = chip.dataset;
        const p = new URLSearchParams();
        p.set('title', d.title || '');
        p.set('start_date', d.start || '');
        p.set('end_date', d.end || '');
        if (d.allday === '1') {
            p.set('all_day', 'on');
        } else {
            p.set('start_time', d.stime || '');
            p.set('end_time', d.etime || '');
        }
        p.set('color', d.color || 'c1');
        if (d.shareAll === '1') p.set('share_all', '1');
        p.set('memo', d.memo || '');
        p.set('calendar_id', d.calid || '');
        p.set('parent_id', d.parent || '');
        if (d.done === '1') p.set('done', 'on');
        (d.shareUsers || '').split(',').filter(Boolean).forEach((v) => p.append('share_users', v));
        (d.shareDepts || '').split(',').filter(Boolean).forEach((v) => p.append('share_depts', v));
        (d.shareGroups || '').split(',').filter(Boolean).forEach((v) => p.append('share_groups', v));
        (d.deps || '').split(',').filter(Boolean).forEach((v) => p.append('dep_ids', v));
        p.set('props_json', d.props || '');
        p.set('reminder', d.reminder || '-1');
        return p;
    }
    function recordUpdate() {
        if (!editingEventID || !editBefore) return;
        const after = snapshotForm();
        if (sessionCmd) {
            sessionCmd.after = after;
            return;
        }
        sessionCmd = updateCommand(editingEventID, editBefore, after);
        history.push(sessionCmd);
    }

    // 명시적 저장: 리다이렉트 대신 fetch → 히스토리 기록 → 그리드 소프트 갱신.
    form.addEventListener('submit', (e) => {
        e.preventDefault();
        const startValid = startTimeControl.validate();
        const endValid = endTimeControl.validate();
        if (!startValid || !endValid) { form.reportValidity(); return; }
        normalizeTimes('start');
        syncPropsJson();
        const isCreate = new URL(form.action, window.location.href).pathname === '/calendar/events';
        // 서버 HTML 미니파이어가 기본값인 type="submit" 을 지우므로 selector 로 찾지 않는다.
        const submitBtn = e.submitter || form.querySelector('button.btn-primary');
        if (submitBtn) submitBtn.disabled = true;
        const L = labels();
        const restoreBtn = () => {
            if (submitBtn && mode !== 'edit') submitBtn.disabled = false;
            else if (submitBtn) submitBtn.disabled = !dirty;
        };
        // 반복 인스턴스의 저장은 범위를 먼저 묻는다 (반복 스펙 §6.2); 규칙을 바꿨으면 「이 일정만」은 없다.
        const isInstance = !isCreate && !!(currentChip && currentChip.dataset.series);
        const ruleChanged = !!(repeat && repeat.isChanged());
        const scopeP = isInstance ? askSeriesScope(ruleChanged ? 'rule' : 'edit') : Promise.resolve('this');
        scopeP.then((scope) => {
            if (scope === null) { restoreBtn(); return null; }
            const fd = new FormData(form);
            if (isInstance) fd.set('series_scope', scope);
            const req = isCreate ? api.create(fd) : api.update(editingEventID, fd);
            return req.then((ev) => {
                if (isCreate) {
                    savedAny = false;
                    closeModalOnly();
                    if (ev && ev.series) { // 시리즈 생성은 실행 취소 밖 (첫 인스턴스만 되돌리면 나머지가 남는다)
                        showToast(fmtN(RL.msgCreated || '반복 일정 %d개를 만들었습니다', ev.created_count || 1));
                        return refreshOrReload();
                    }
                    return record(createCommand(ev.id), L.created);
                }
                if (scope === 'this') recordUpdate();
                else {
                    seriesCache = {};
                    showToast(fmtN(RL.msgChanged || '일정 %d개를 바꿨습니다', ((ev && ev.affected) || 0) + 1));
                }
                savedAny = false;
                setDirty(false);
                flashSaved();
                const id = editingEventID;
                // 저장 뒤엔 읽기 모드로 돌아간다 (새 값으로 다시 연다).
                return refreshOrReload().then(() => {
                    const chip = findEventChip(id);
                    if (chip) window.openCalendarEvent(chip, { noPush: true, replaceURL: true });
                    else closeModalOnly();
                });
            }).catch(() => {
                setSaveState('failed');
            }).finally(restoreBtn);
        });
    });

    // ── URL 동기화: 열린 일정은 ?event=<id> 로 주소에 남아 공유·새로고침·뒤로가기가 된다 ──
    function eventURL(id, edit) {
        const u = new URL(window.location.href);
        if (id) u.searchParams.set('event', id);
        else u.searchParams.delete('event');
        if (id && edit) u.searchParams.set('edit', '1');
        else u.searchParams.delete('edit');
        return u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '');
    }
    function currentURLEdit() {
        return new URL(window.location.href).searchParams.get('edit') === '1';
    }
    function currentURLEvent() {
        return new URL(window.location.href).searchParams.get('event') || '';
    }
    function dropURLEvent() {
        if (currentURLEvent()) window.history.replaceState({}, '', eventURL(''));
    }
    function findEventChip(id) {
        if (!id) return null;
        return document.querySelector('[data-id="' + CSS.escape(id) + '"][data-title]');
    }
    const copyBtn = document.getElementById('calCopyLink');
    if (copyBtn) {
        copyBtn.addEventListener('click', () => {
            const id = copyBtn.dataset.eventId;
            if (!id) return;
            const link = window.location.origin + '/calendar/events/' + encodeURIComponent(id);
            const done = () => showToast(modal.dataset.msgLinkCopied || '링크를 복사했습니다');
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(link).then(done).catch(done);
            } else {
                const ta = document.createElement('textarea');
                ta.value = link;
                document.body.appendChild(ta);
                ta.select();
                try { document.execCommand('copy'); } catch (_) { /* 무시 */ }
                ta.remove();
                done();
            }
        });
    }

    function closeModalOnly() {
        startTimeControl.close();
        endTimeControl.close();
        clearTimeout(saveTimer);
        modal.style.display = 'none';
        document.body.classList.remove('modal-scroll-lock');
        dirty = false;
        setSaveState(null);
        dropURLEvent();
    }
    // dirty 면 인앱 확인 뒤에만 닫힌다.
    function confirmDiscard() {
        if (!dirty || mode === 'read') return Promise.resolve(true);
        return appConfirm({
            message: modal.dataset.msgDiscard || '저장하지 않은 변경이 있습니다. 버릴까요?',
            okLabel: modal.dataset.msgDiscardOk || '버리기',
            cancelLabel: modal.dataset.msgKeepEditing || '계속 편집',
            danger: true,
        });
    }

    window.closeCalendarModal = function () {
        confirmDiscard().then((ok) => {
            if (!ok) return;
            closeModalOnly();
            if (savedAny) {
                savedAny = false;
                refreshOrReload();
            }
        });
    };

    const readPane = document.getElementById('calPeekRead');
    function showPane(readMode) {
        if (readPane) readPane.hidden = !readMode;
        form.hidden = readMode;
        modal.classList.toggle('is-read', readMode);
        // 읽기 모드에선 페이지가 살아 있으니 스크롤도 잠그지 않는다.
        document.body.classList.toggle('modal-scroll-lock', !readMode && modal.style.display === 'flex');
    }
    function openModal() {
        document.getElementById('calendarCsrf').value = window.getCSRFToken ? window.getCSRFToken() : '';
        // 하단 토스트/선택 바가 모달 푸터(저장 버튼)를 가리지 않게 치운다.
        dismissToast();
        if (window.calendarGridClearSelection) window.calendarGridClearSelection();
        modal.style.display = 'flex';
        // 배경 스크롤 잠금 (편집/작성 모드만): 모달과 페이지 스크롤바가 겹쳐 보이지 않게.
        document.body.classList.toggle('modal-scroll-lock', mode !== 'read');
    }

    function resetShared() {
        panel.hidden = true;
        shareAdd.setAttribute('aria-expanded', 'false');
        shareSearch.value = '';
        renderChips();
        savedAny = false;
        setSaveState(null);
        clearTimeout(saveTimer);
    }

    window.openCalendarCreate = function (date, endDate) {
        form.reset();
        form.action = '/calendar/events';
        mode = 'create';
        showPane(false);
        setDirty(false);
        hideTitleDetect();
        if (saveBtn) saveBtn.disabled = false;
        if (calSel) syncSelect(calSel);
        const remSelC = document.getElementById('calReminder');
        if (remSelC) { remSelC.value = '-1'; syncSelect(remSelC); }
        loadProps('');
        modalTitle.textContent = modalTitle.dataset.addLabel || modalTitle.textContent;
        // date === null 이면 날짜 미정으로 연다 (프로젝트 허브의 새 작업)
        const today = date === null ? '' : (date || localISODate(new Date()));
        startEl.value = today;
        endEl.value = date === null ? '' : (endDate || today);
        syncDates();
        if (repeat) { repeat.reset(); repeat.setDtstart(today); }
        document.getElementById('calAllDay').checked = true;
        setTime(sTime, '', '09:00');
        setTime(eTime, '', '10:00');
        window.toggleCalendarTimes();
        setMulti(selects.users, '');
        setMulti(selects.depts, '');
        setMulti(selects.groups, '');
        shareAll.value = '0';
        document.getElementById('calDeleteBtn').style.display = 'none';
        document.getElementById('calDupBtn').style.display = 'none';
        document.getElementById('calOwnerLine').style.display = 'none';
        editingEventID = '';
        editBefore = null;
        sessionCmd = null;
        if (copyBtn) copyBtn.hidden = true;
        const openPageC = document.getElementById('calOpenPage');
        if (openPageC) openPageC.hidden = true;
        const readEditC = document.getElementById('calReadEdit');
        if (readEditC) readEditC.hidden = true;
        const projC = document.getElementById('calPeekProject');
        if (projC) projC.hidden = true;
        dropURLEvent();
        const doneC = document.getElementById('calDone');
        if (doneC) { doneC.checked = false; doneC.className = 'todo-check'; }
        refreshParentOptions('', '', '', '');
        resetShared();
        openModal();
        titleEl.focus();
    };

    window.openCalendarEvent = function (chip, opts) {
        const o = opts || {};
        form.reset();
        form.action = '/calendar/events/' + chip.dataset.id + '/update';
        mode = o.edit ? 'edit' : 'read';
        currentChip = chip;
        hideTitleDetect();
        if (o.replaceURL) window.history.replaceState({ calEvent: chip.dataset.id }, '', eventURL(chip.dataset.id, !!o.edit));
        else if (!o.noPush && (currentURLEvent() !== chip.dataset.id || currentURLEdit() !== !!o.edit)) {
            window.history.pushState({ calEvent: chip.dataset.id }, '', eventURL(chip.dataset.id, !!o.edit));
        }
        fillRead(chip);
        showPane(mode === 'read');
        const readEditBtn = document.getElementById('calReadEdit');
        if (readEditBtn) readEditBtn.hidden = mode !== 'read';
        modalTitle.textContent = (mode === 'read' ? modalTitle.dataset.readLabel : modalTitle.dataset.editLabel) || modalTitle.textContent;
        if (copyBtn) {
            copyBtn.hidden = false;
            copyBtn.dataset.eventId = chip.dataset.id;
        }
        const openPage = document.getElementById('calOpenPage');
        if (openPage) openPage.hidden = false;
        titleEl.value = chip.dataset.title;
        startEl.value = chip.dataset.start;
        endEl.value = chip.dataset.end;
        syncDates();
        const allDay = chip.dataset.allday === '1';
        document.getElementById('calAllDay').checked = allDay;
        setTime(sTime, chip.dataset.stime, '09:00');
        setTime(eTime, chip.dataset.etime, '10:00');
        window.toggleCalendarTimes();
        const colorInput = document.querySelector('#calColors input[value="' + chip.dataset.color + '"]');
        if (colorInput) colorInput.checked = true;
        if (calSel) {
            calSel.value = chip.dataset.calid || '';
            syncSelect(calSel);
        }
        const remSel = document.getElementById('calReminder');
        if (remSel) {
            remSel.value = chip.dataset.reminder || '-1';
            syncSelect(remSel);
        }
        loadProps(chip.dataset.props || '');
        // 반복: 인스턴스면 시리즈 규칙을 받아 잠긴 요약(+「규칙 바꾸기…」)으로, 아니면 「없음」
        if (repeat) {
            if (chip.dataset.series) {
                repeat.lock(null, chip.dataset.start);
                loadSeries(chip.dataset.series).then((j) => {
                    if (!j || currentChip !== chip) return;
                    repeat.lock(j.rule, j.dtstart);
                    const readRow = document.querySelector('#calReadProps [data-field="repeat"] .cal-prop-val');
                    if (readRow) readRow.textContent = j.summary || '';
                });
            } else {
                repeat.reset();
                repeat.setDtstart(chip.dataset.start);
            }
        }
        // 완료는 모든 일정의 할 일 체크다 — 프로젝트 작업만이 아니다 (2026-09-17 #1).
        const doneCb = document.getElementById('calDone');
        if (doneCb) { doneCb.checked = chip.dataset.done === '1'; doneCb.className = 'todo-check cal-' + (chip.dataset.color || 'c1'); }
        editingEventID = chip.dataset.id;
        editBefore = paramsFromChip(chip);
        sessionCmd = null;
        refreshParentOptions(chip.dataset.calid || '', chip.dataset.id, chip.dataset.parent || '', chip.dataset.deps || '');
        document.getElementById('calMemo').value = chip.dataset.memo || '';
        setMulti(selects.users, chip.dataset.shareUsers, missingPeopleOf(chip.dataset)); // 비활성 공유 대상은 ⚠ 로 남긴다 (Z3)
        setMulti(selects.depts, chip.dataset.shareDepts);
        setMulti(selects.groups, chip.dataset.shareGroups);
        shareAll.value = chip.dataset.shareAll === '1' ? '1' : '0';
        const del = document.getElementById('calDeleteBtn');
        del.style.display = chip.dataset.isowner === '1' ? 'inline-flex' : 'none';
        del.dataset.eventId = chip.dataset.id;
        document.getElementById('calDupBtn').style.display = 'inline-flex';
        const ownerLine = document.getElementById('calOwnerLine');
        if (chip.dataset.owner) {
            ownerLine.innerHTML = CAL_ICONS.users;
            ownerLine.appendChild(document.createTextNode(
                (ownerLine.dataset.ownerLabel ? ownerLine.dataset.ownerLabel + ' · ' : '') + chip.dataset.owner
            ));
            ownerLine.style.display = 'inline-flex';
        } else {
            ownerLine.style.display = 'none';
        }
        resetShared();
        setDirty(false);
        setSaveState(null);
        openModal();
        loadReadHistory(chip.dataset.id);
        if (mode === 'edit') titleEl.focus();
    };

    // ── 추가 속성 편집기: 이름 · 유형 · 값 행들, hidden props_json 이 원본 ──
    const propsBox = document.getElementById('calProps');
    const propsJson = document.getElementById('calPropsJson');
    const propsAdd = document.getElementById('calPropsAdd');
    const PROP_TYPES = ['text', 'number', 'date', 'url', 'check', 'user'];
    function readProps() {
        if (!propsBox) return [];
        return Array.from(propsBox.querySelectorAll('.cal-prop-row')).map((r) => {
            const type = r.querySelector('.cal-prop-type').value;
            const valEl = r.querySelector('[data-prop-value]');
            let value = '';
            if (type === 'check') value = valEl.checked ? '1' : '';
            else value = valEl.value || '';
            return { name: r.querySelector('.cal-prop-name').value.trim(), type, value };
        }).filter((p) => p.name);
    }
    let syncPropsJson = function () {
        if (!propsJson) return;
        const list = readProps();
        propsJson.value = list.length ? JSON.stringify(list) : '';
    };
    function buildValueField(type, value) {
        if (type === 'check') {
            const c = document.createElement('input');
            c.type = 'checkbox';
            c.checked = value === '1';
            c.setAttribute('data-prop-value', '');
            c.className = 'cal-prop-check';
            return c;
        }
        if (type === 'user') {
            const sel = document.createElement('select');
            sel.setAttribute('data-prop-value', '');
            const none = document.createElement('option');
            none.value = '';
            none.textContent = '—';
            sel.appendChild(none);
            if (selects.users) {
                Array.from(selects.users.options).forEach((o) => {
                    const op = document.createElement('option');
                    op.value = o.value;
                    op.textContent = o.textContent.trim();
                    sel.appendChild(op);
                });
            }
            sel.value = value || '';
            sel.setAttribute('data-inapp-select', '');
            return sel;
        }
        const inp = document.createElement('input');
        inp.type = type === 'number' ? 'number' : (type === 'url' ? 'url' : 'text');
        if (type === 'date') { inp.type = 'text'; inp.placeholder = 'YYYY-MM-DD'; inp.pattern = '\\d{4}-\\d{2}-\\d{2}'; }
        inp.value = value || '';
        inp.maxLength = 500;
        inp.className = 'form-input cal-prop-value';
        inp.placeholder = inp.placeholder || (propsBox ? propsBox.dataset.valuePh : '');
        inp.setAttribute('data-prop-value', '');
        return inp;
    }
    function addPropRow(prop) {
        if (!propsBox) return null;
        const pr = prop || { name: '', type: 'text', value: '' };
        // 다른 속성 행과 같은 그리드: 라벨 칸에 이름·유형, 값 칸에 값·삭제 (2026-09-17 #5).
        const row = document.createElement('div');
        row.className = 'cal-prop cal-prop-row';
        const head = document.createElement('div');
        head.className = 'cal-prop-head';
        const name = document.createElement('input');
        name.type = 'text';
        name.className = 'cal-prop-name';
        name.maxLength = 40;
        name.placeholder = propsBox.dataset.namePh || '';
        name.setAttribute('aria-label', propsBox.dataset.namePh || 'name');
        name.value = pr.name || '';
        const type = document.createElement('select');
        type.className = 'cal-prop-type';
        type.setAttribute('aria-label', propsBox.dataset.lType || 'type');
        PROP_TYPES.forEach((t) => {
            const o = document.createElement('option');
            o.value = t;
            o.textContent = propsBox.dataset['t' + t.charAt(0).toUpperCase() + t.slice(1)] || t;
            type.appendChild(o);
        });
        type.value = PROP_TYPES.indexOf(pr.type) >= 0 ? pr.type : 'text';
        type.setAttribute('data-inapp-select', '');
        head.appendChild(name);
        head.appendChild(type);
        const val = document.createElement('div');
        val.className = 'cal-prop-val';
        const valWrap = document.createElement('span');
        valWrap.className = 'cal-prop-valwrap';
        valWrap.appendChild(buildValueField(type.value, pr.value));
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'cal-icon-btn cal-prop-rm';
        rm.title = propsBox.dataset.lRemove || 'remove';
        rm.setAttribute('aria-label', propsBox.dataset.lRemove || 'remove');
        rm.textContent = '✕';
        rm.addEventListener('click', () => { row.remove(); syncPropsJson(); markDirty(); });
        val.appendChild(valWrap);
        val.appendChild(rm);
        row.appendChild(head);
        row.appendChild(val);
        propsBox.appendChild(row);
        initInappSelects(row);
        type.addEventListener('change', () => {
            const cur = valWrap.querySelector('[data-prop-value]');
            const keep = cur && cur.type !== 'checkbox' ? cur.value : '';
            valWrap.innerHTML = '';
            valWrap.appendChild(buildValueField(type.value, keep));
            initInappSelects(valWrap);
            syncPropsJson();
        });
        return row;
    }
    let loadProps = function (json) {
        if (!propsBox) return;
        propsBox.innerHTML = '';
        let list = [];
        try { list = JSON.parse(json || '[]') || []; } catch (_) { list = []; }
        list.forEach((p) => addPropRow(p));
        syncPropsJson();
    };
    // 「＋ 커스텀 속성」: 개인 일정의 속성은 모든 내 일정이 공유하는 열이다 (속성 spec §8).
    // 자유 속성 행(레거시)은 이미 있는 일정에서만 「＋ 속성 추가」(data-legacy)로 이어 붙인다.
    const myPropAdd = document.getElementById('calMyPropAdd');
    const propsAddLegacy = document.getElementById('calPropsAddLegacy');
    if (propsAddLegacy) {
        propsAddLegacy.addEventListener('click', () => {
            const row = addPropRow(null);
            if (row) row.querySelector('.cal-prop-name').focus();
            markDirty();
        });
    }
    const propertyCreate = bindPropertyCreate(propsAdd, myPropAdd, (property) => {
        schemas[''] = (schemas[''] || []).concat([property]);
        renderSchemaRows();
        const last = schemaBox?.lastElementChild?.querySelector('input, button, select');
        if (last) last.focus();
    });
    form.addEventListener('input', () => syncPropsJson());
    form.addEventListener('change', () => syncPropsJson());

    // ── 프로젝트 속성(스키마) 행: 선택된 캘린더의 속성을 유형별 컨트롤로 ──
    let schemas = {};
    try { schemas = JSON.parse(modal.dataset.schemas || '{}') || {}; } catch (_) { schemas = {}; }
    const schemaBox = document.getElementById('calSchemaProps');
    const adhocRow = document.getElementById('calAdhocProps');
    const calSelEl = document.getElementById('calCalSel'); // calSel(const) 는 아래에서 선언되므로 TDZ 회피
    let schemaValues = {}; // property id → value (multiselect: JSON array string)
    const usersList = () => { const sel = document.getElementById('calAllUsers') || selects.users; return sel ? Array.from(sel.options).map((o) => ({ label: o.textContent.trim(), value: o.value })) : []; };
    function schemaFor(calId) {
        return schemas[calId || ''] || []; // '' = 내 속성 (모든 내 일정이 공유, 속성 spec §8)
    }
    // 읽기 시점에 계산된 값 (관계·롤업·자동 번호): 행/칩의 data-computed 섬
    function computedOf(chip) {
        try { return JSON.parse((chip && chip.dataset.computed) || '{}') || {}; } catch (_) { return {}; }
    }
    const COMPUTED_TYPES = ['rollup', 'autonumber', 'formula'];
    // 계산 실패 ⚠: 툴팁·접근 이름은 서버가 번역한 이유 (수식 spec §16)
    function computedErr(cv) {
        const s = document.createElement('span');
        s.className = 'cal-prop-err';
        s.setAttribute('role', 'img');
        s.title = cv.err_t || cv.err;
        s.setAttribute('aria-label', s.title);
        s.textContent = '⚠';
        return s;
    }
    function currentCalId() {
        return calSelEl ? calSelEl.value : '';
    }
    function setSchemaValue(id, v) {
        if (v === '' || v == null) delete schemaValues[id];
        else schemaValues[id] = v;
        syncPropsJson();
        markDirty();
    }
    function pillButton(def, value, multi) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cal-pillbtn';
        const vals = multi ? parseMulti(value) : (value ? [value] : []);
        function paint() {
            btn.innerHTML = '';
            const cur = multi ? parseMulti(schemaValues[def.id] || '') : (schemaValues[def.id] ? [schemaValues[def.id]] : []);
            if (!cur.length) {
                const ph = document.createElement('span');
                ph.className = 'placeholder';
                ph.textContent = modal.dataset.lPick || '선택…';
                btn.appendChild(ph);
                return;
            }
            cur.forEach((v) => {
                if (def.type === 'user') {
                    const u = usersList().find((x) => x.value === v);
                    btn.appendChild(pill(u ? u.label : v, '', true));
                } else {
                    const o = (def.options || []).find((x) => x.label === v);
                    btn.appendChild(pill(v, o ? o.color : 'o8'));
                }
            });
        }
        paint();
        btn.addEventListener('click', () => {
            if (def.type === 'user') {
                const users = usersList();
                const curV = schemaValues[def.id] || '';
                openPillPicker(btn, {
                    options: users.map((u) => ({ label: u.label, color: '' })), neutral: true,
                    values: (() => { const u = users.find((x) => x.value === curV); return u ? [u.label] : []; })(),
                    noneLabel: modal.dataset.lNone, placeholder: modal.dataset.lPick,
                    onChange: (labels) => { const u = users.find((x) => x.label === labels[0]); setSchemaValue(def.id, u ? u.value : ''); paint(); },
                });
                return;
            }
            openPillPicker(btn, {
                options: def.options || [], multi, allowNew: true,
                values: multi ? parseMulti(schemaValues[def.id] || '') : (schemaValues[def.id] ? [schemaValues[def.id]] : []),
                noneLabel: modal.dataset.lNone, newLabel: modal.dataset.lNewOption, placeholder: modal.dataset.lPick,
                onNew: (opt) => { def.options = (def.options || []).concat([opt]); },
                onChange: (labels) => { setSchemaValue(def.id, multi ? (labels.length ? JSON.stringify(labels) : '') : (labels[0] || '')); paint(); },
            });
        });
        void vals;
        return btn;
    }
    function parseMulti(v) {
        if (!v) return [];
        try { const a = JSON.parse(v); return Array.isArray(a) ? a : [String(v)]; } catch (_) { return [v]; }
    }
    function schemaControl(def) {
        const v = schemaValues[def.id] || '';
        if (def.type === 'select' || def.type === 'multiselect' || def.type === 'user') return pillButton(def, v, def.type === 'multiselect');
        if (COMPUTED_TYPES.includes(def.type)) {
            // 계산되는 값: 읽기 전용 — 저장한 뒤 서버가 다시 센다
            const cv = computedOf(currentChip)[def.id] || {};
            if (cv.err) return computedErr(cv);
            const s = document.createElement('span');
            s.className = 'cal-prop-readonly text-muted';
            s.title = modal.dataset.lReadonly || '';
            s.textContent = cv.t || '—';
            return s;
        }
        if (def.type === 'status') {
            const sel = document.createElement('select');
            sel.setAttribute('data-inapp-select', '');
            sel.className = 'cal-prop-status';
            const none = document.createElement('option'); none.value = ''; none.textContent = '—'; sel.appendChild(none);
            ['todo', 'doing', 'done'].forEach((g) => {
                const og = document.createElement('optgroup');
                og.label = modal.dataset['lGroup' + g.charAt(0).toUpperCase() + g.slice(1)] || g;
                (def.options || []).filter((o) => (o.group || 'todo') === g).forEach((o) => { const op = document.createElement('option'); op.value = o.label; op.textContent = o.label; og.appendChild(op); });
                if (og.children.length) sel.appendChild(og);
            });
            sel.value = v;
            sel.addEventListener('change', () => setSchemaValue(def.id, sel.value));
            return sel;
        }
        if (def.type === 'users') {
            const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'cal-pillbtn cal-usersbtn';
            const paint = () => {
                btn.innerHTML = '';
                const ids = parseMulti(schemaValues[def.id] || '');
                if (!ids.length) { const ph = document.createElement('span'); ph.className = 'placeholder'; ph.textContent = modal.dataset.lPick || '선택…'; btn.appendChild(ph); return; }
                ids.forEach((id) => { const u = usersList().find((x) => x.value === id); btn.appendChild(pill(u ? u.label : id, '', true)); });
            };
            paint();
            btn.addEventListener('click', () => openPeoplePicker(btn, { multi: true, allowMe: false, values: parseMulti(schemaValues[def.id] || ''), onChange: (ids) => { const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean); setSchemaValue(def.id, list.length ? JSON.stringify(list) : ''); paint(); } }));
            return btn;
        }
        if (def.type === 'relation') {
            const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'cal-pillbtn cal-relbtn';
            const cv = computedOf(currentChip)[def.id] || {};
            let items = (cv.ids || []).map((id, i) => ({ id, title: (cv.titles || [])[i] || '…' }));
            // 관계는 링크 표에 살아서 props_json 에 없다 — 편집 시작값은 계산 섬에서
            if (!schemaValues[def.id] && items.length) schemaValues[def.id] = JSON.stringify(items.map((x) => x.id));
            const paint = () => {
                btn.innerHTML = '';
                if (!items.length && !cv.hidden) { const ph = document.createElement('span'); ph.className = 'placeholder'; ph.textContent = modal.dataset.lPickEvents || modal.dataset.lPick || '선택…'; btn.appendChild(ph); return; }
                items.forEach((it) => btn.appendChild(pill(it.title, '', true)));
                if (cv.hidden) btn.appendChild(pill('🔒 ' + (modal.dataset.lPrivateN || '%s').replace('%s', String(cv.hidden)), '', true));
            };
            paint();
            const target = (def.config && def.config.target) || '';
            const scope = target === 'me' || !target ? (currentCalId() || 'me') : target;
            btn.addEventListener('click', () => openEventPicker(btn, {
                scope, multi: !(def.config && def.config.single), values: items,
                exclude: currentChip ? [currentChip.dataset.id] : [],
                labels: { search: modal.dataset.lPickEvents, empty: modal.dataset.lNoMatch },
                onChange: (list) => { items = list; schemaValues[def.id] = JSON.stringify(list.map((x) => x.id)); syncPropsJson(); markDirty(); paint(); },
            }));
            return btn;
        }
        if (def.type === 'files') {
            // 파일 (AY5): 미리보기 줄 · 여러 파일 고르기 · 끌어 놓기 — 올리기·지우기는 곧바로 서버로(실행 취소 밖)
            const box = document.createElement('div'); box.className = 'cal-prop-filebox'; box.setAttribute('data-file-group', '');
            const refs = () => { try { return JSON.parse(schemaValues[def.id] || '[]') || []; } catch (_) { return []; } };
            const evId = currentChip && currentChip.dataset.id;
            const send = window.csrfFetch || fetch;
            let paint = () => {};
            const setRefs = (list) => { schemaValues[def.id] = (list && list.length) ? JSON.stringify(list) : ''; syncPropsJson(); paint(); };
            const upload = (files) => {
                if (!evId || !files || !files.length) return;
                box.classList.add('is-busy');
                uploadFiles(evId, def.id, files, { current: refs(), send, labels: { limit: modal.dataset.lFileLimit, tooLarge: modal.dataset.lFileTooLarge }, onProgress: setRefs })
                    .then((res) => { box.classList.remove('is-busy'); setRefs(res.props); if (res.errors.length) showToast(res.errors.join(' · '), { error: true }); });
            };
            const remove = (f, x) => {
                x.disabled = true;
                send('/calendar/events/' + evId + '/files/' + encodeURIComponent(f.id) + '/delete', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } })
                    .then((r) => r.json().catch(() => ({})).then((j) => ({ ok: r.ok, j })))
                    .then(({ ok, j }) => { if (!ok) { x.disabled = false; showToast(j.message || labels().failed, { error: true }); return; } setRefs(j.props || []); })
                    .catch(() => { x.disabled = false; showToast(labels().failed, { error: true }); });
            };
            paint = () => {
                box.innerHTML = '';
                refs().forEach((f) => box.appendChild(fileTile(evId, f, { onRemove: remove })));
                if (!evId) { box.appendChild(mutedSpan(modal.dataset.lFileSaveFirst || '저장한 뒤 첨부할 수 있습니다')); return; }
                if (refs().length >= CAL_MAX_FILES) return;
                const lab = document.createElement('label'); lab.className = 'cal-prop-fileadd';
                const inp = document.createElement('input'); inp.type = 'file'; inp.multiple = true; inp.className = 'cal-prop-fileinput'; inp.setAttribute('aria-label', modal.dataset.lFileAdd || '파일 추가');
                inp.addEventListener('change', () => { const files = Array.from(inp.files || []); inp.value = ''; upload(files); });
                lab.appendChild(inp);
                lab.appendChild(mutedSpan(modal.dataset.lFileDrop || '여러 개 고르거나 여기로 끌어 놓기'));
                box.appendChild(lab);
            };
            if (evId) bindDrop(box, upload);
            paint();
            return box;
        }
        if (def.type === 'check') {
            const c = document.createElement('input');
            c.type = 'checkbox';
            c.className = 'cal-prop-check';
            c.checked = v === '1';
            c.addEventListener('change', () => setSchemaValue(def.id, c.checked ? '1' : ''));
            return c;
        }
        if (def.type === 'date') {
            const wrap = document.createElement('div');
            wrap.className = 'att-dp';
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.value = v;
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'form-input att-dp-btn';
            const lbl = document.createElement('span');
            lbl.className = 'att-dp-label text-muted';
            b.appendChild(lbl);
            wrap.appendChild(hidden);
            wrap.appendChild(b);
            syncDateLabel(b, hidden, modal.dataset.pickDate);
            attachDatePicker(b, hidden, { weekdays: modal.dataset.weekdays, weekStart: modal.dataset.weekStart, placeholder: modal.dataset.pickDate, onSet: (val) => { setSchemaValue(def.id, val); syncDateLabel(b, hidden, modal.dataset.pickDate); } });
            return wrap;
        }
        const inp = document.createElement('input');
        inp.type = def.type === 'number' ? 'number' : (def.type === 'url' ? 'url' : 'text');
        inp.className = 'form-input cal-prop-value';
        inp.value = v;
        inp.maxLength = 500;
        inp.addEventListener('input', () => setSchemaValue(def.id, inp.value.trim()));
        return inp;
    }
    function renderSchemaRows() {
        if (!schemaBox) return;
        schemaBox.innerHTML = '';
        const calId = currentCalId();
        const list = schemaFor(calId);
        if (adhocRow) adhocRow.hidden = !!calId; // 프로젝트 작업은 스키마 속성만
        if (propsAddLegacy) propsAddLegacy.hidden = !!calId || readProps().length === 0; // 자유 속성은 이미 있는 일정에서만
        if (calId && propertyCreate) propertyCreate.reset();
        list.forEach((def) => {
            const row = document.createElement('div');
            row.className = 'cal-prop' + (def.type === 'multiselect' ? ' cal-prop-multi' : '');
            const label = document.createElement('label');
            label.textContent = def.name;
            const val = document.createElement('div');
            val.className = 'cal-prop-val';
            val.appendChild(schemaControl(def));
            row.appendChild(label);
            row.appendChild(val);
            schemaBox.appendChild(row);
        });
        syncPropsJson();
    }
    if (calSelEl) calSelEl.addEventListener('change', renderSchemaRows);
    // props_json: 스키마 값(id) + (캘린더 없을 때만) 자유 속성
    const syncPropsJsonBase = syncPropsJson;
    syncPropsJson = function () {
        if (!propsJson) return;
        const calId = currentCalId();
        const list = [];
        Object.keys(schemaValues).forEach((id) => { if (schemaValues[id] !== '') list.push({ id, value: schemaValues[id] }); });
        if (!calId) readProps().forEach((p) => list.push(p));
        propsJson.value = list.length ? JSON.stringify(list) : '';
    };
    void syncPropsJsonBase;
    const loadPropsBase = loadProps;
    loadProps = function (json) {
        if (propertyCreate) propertyCreate.reset();
        let list = [];
        try { list = JSON.parse(json || '[]') || []; } catch (_) { list = []; }
        schemaValues = {};
        list.filter((p) => p.id).forEach((p) => { schemaValues[p.id] = p.value; });
        loadPropsBase(JSON.stringify(list.filter((p) => !p.id)));
        renderSchemaRows();
    };

    // ── 읽기 모드 뷰 ──
    let currentChip = null;
    // 읽기 패널의 행은 편집 폼의 [data-field] 순서를 그대로 따른다 — 순서·라벨의 원본이
    // 폼 하나라 두 화면이 어긋나지 않는다 (2026-09-17 #4). 값이 없는 행도 그리되 "—" 로
    // 보이고, 클릭하면 그 칸부터 편집이다.
    function formFieldOrder() {
        const seen = new Set();
        return Array.from(form.querySelectorAll('.cal-props [data-field]'))
            .map((el) => el.dataset.field)
            .filter((f) => f && !seen.has(f) && seen.add(f));
    }
    function formLabel(field, fallback) {
        const l = form.querySelector('.cal-props [data-field="' + field + '"] > label');
        const text = l ? l.textContent.trim() : '';
        return text || fallback || field;
    }
    function mutedSpan(text) {
        const s = document.createElement('span');
        s.className = 'text-muted';
        s.textContent = text;
        return s;
    }
    function optionTextOf(sel, value) {
        const o = sel && Array.from(sel.options).find((op) => op.value === value);
        return o ? o.textContent.trim() : '';
    }
    // 상위·선행 일정의 제목은 그 프로젝트의 일정 목록을 받은 뒤에야 안다 — refreshParentOptions 가
    // 끝나면 다시 칠한다 (읽기 패널이 열려 있을 때만 대상이 있다).
    function paintReadLinks() {
        const p = document.querySelector('#calReadProps [data-read-parent]');
        if (p) {
            const v = p.dataset.readParent || '';
            p.innerHTML = '';
            if (v) p.textContent = optionTextOf(parentSel, v) || '…';
            else p.appendChild(mutedSpan((parentSel && parentSel.dataset.noneLabel) || '—'));
        }
        const dbox = document.querySelector('#calReadProps [data-read-deps]');
        if (dbox) {
            dbox.innerHTML = '';
            const ids = (dbox.dataset.readDeps || '').split(',').filter(Boolean);
            if (!ids.length) dbox.appendChild(mutedSpan('—'));
            ids.forEach((id) => {
                const c = document.createElement('span');
                c.className = 'cal-share-chip';
                const t = document.createElement('span');
                t.textContent = depTitles[id] || '…';
                c.appendChild(t);
                dbox.appendChild(c);
            });
        }
    }
    function optionLabels(sel, csv) {
        const wanted = (csv || '').split(',').filter(Boolean);
        return wanted.map((v) => {
            const o = sel && Array.from(sel.options).find((op) => op.value === v);
            return o ? o.textContent.trim() : '';
        }).filter(Boolean);
    }
    function fillRead(chip) {
        const props = document.getElementById('calReadProps');
        const titleH = document.getElementById('calReadTitle');
        if (!props || !titleH) return;
        const d = chip.dataset;
        const L = props.dataset;
        titleH.innerHTML = '';
        titleH.appendChild(document.createTextNode(d.title || ''));
        titleH.classList.toggle('is-done', d.done === '1');
        if (d.hidden === '1') {
            const b = document.createElement('span');
            b.className = 'badge badge-secondary';
            b.textContent = modal.dataset.lHidden || '숨김';
            titleH.appendChild(b);
        }
        // 체크 링이 캘린더 색을 입는다 — 예전의 색 점은 빠졌다 (2026-09-18).
        const readDone = document.getElementById('calReadDone');
        if (readDone) { readDone.checked = d.done === '1'; readDone.className = 'todo-check cal-' + (d.color || 'c1'); }
        paintDoneMeta([]);
        const hideBtn = document.getElementById('calReadHide');
        if (hideBtn) hideBtn.textContent = d.hidden === '1' ? (hideBtn.dataset.lUnhide || '숨기기 해제') : (hideBtn.dataset.lHide || '숨기기');
        props.innerHTML = '';
        // 값을 클릭하면 그 항목부터 편집 (field 가 null 이면 편집 대상 아님)
        function row(label, value, field) {
            const r = document.createElement('div');
            r.className = 'cal-prop' + (field ? ' cal-prop-editable' : '');
            if (field) r.dataset.field = field;
            const l = document.createElement('label');
            l.textContent = label;
            const v = document.createElement('div');
            v.className = 'cal-prop-val';
            if (typeof value === 'string') v.textContent = value;
            else v.appendChild(value);
            r.appendChild(l);
            r.appendChild(v);
            props.appendChild(r);
        }
        const EMPTY = '—';
        const renderers = {
            start: () => row(formLabel('start', L.lDate), d.start || (modal.dataset.lTbd || '미정'), 'start'),
            end: () => row(formLabel('end', L.lDate), d.end || (modal.dataset.lTbd || '미정'), 'end'),
            time: () => row(formLabel('time', L.lTime), d.allday === '1' ? L.lAllday : ((d.stime || '') + ' – ' + (d.etime || '')), 'time'),
            calendar: () => {
                if (!calSel) return;
                row(formLabel('calendar', L.lCal), optionTextOf(calSel, d.calid || '') || (calSel.options[0] ? calSel.options[0].textContent.trim() : EMPTY), 'calendar');
            },
            parent: () => {
                if (!d.calid) return; // 폼에서도 개인 일정엔 없는 행
                const el = document.createElement('span');
                el.dataset.readParent = d.parent || '';
                row(formLabel('parent'), el, 'parent');
            },
            deps: () => {
                if (!d.calid) return;
                const box = document.createElement('div');
                box.className = 'cal-share-chips';
                box.style.minHeight = '0';
                box.dataset.readDeps = d.deps || '';
                row(formLabel('deps'), box, 'deps');
            },
            color: () => {
                const s = document.createElement('span');
                s.className = 'cal-read-swatch cal-' + (d.color || 'c1');
                row(formLabel('color', L.lColor), s, 'color');
            },
            reminder: () => {
                const remSel = document.getElementById('calReminder');
                if (!remSel) return;
                row(formLabel('reminder'), optionTextOf(remSel, d.reminder || '-1') || EMPTY, 'reminder');
            },
            repeat: () => {
                if (!d.series) return; // 반복 없는 일정은 행 자체를 숨긴다 (반복 스펙 §6.1); 요약은 시리즈를 받은 뒤 채운다
                const s = document.createElement('span');
                s.textContent = '…';
                row(formLabel('repeat'), s, 'repeat');
            },
            share: () => {
                const names = d.shareAll === '1' ? [L.lShareAll] : optionLabels(selects.users, d.shareUsers).concat(optionLabels(selects.depts, d.shareDepts), optionLabels(selects.groups, d.shareGroups));
                const shareBox = document.createElement('div');
                shareBox.className = 'cal-share-chips';
                shareBox.style.minHeight = '0';
                if (names.length) {
                    names.forEach((n) => {
                        const c = document.createElement('span');
                        c.className = 'cal-share-chip';
                        const t = document.createElement('span');
                        t.textContent = n;
                        c.appendChild(t);
                        shareBox.appendChild(c);
                    });
                } else {
                    const t = mutedSpan(L.lShareNone);
                    t.style.fontSize = 'var(--text-sm)';
                    shareBox.appendChild(t);
                }
                row(formLabel('share', L.lShare), shareBox, 'share');
            },
            props: () => {
                let customProps = [];
                try { customProps = JSON.parse(d.props || '[]') || []; } catch (_) { customProps = []; }
                const schemaDefs = schemaFor(d.calid || '');
                const computed = computedOf(chip);
                // 스키마 순서대로 (값 없는 속성은 생략 — 계산·관계 값은 계산 섬에서), 그 뒤 자유 속성
                const ordered = schemaDefs.map((def) => {
                    const stored = customProps.find((p) => p.id === def.id);
                    if (stored) return stored;
                    const cv = computed[def.id];
                    if (cv && (COMPUTED_TYPES.includes(def.type) || def.type === 'relation')) return { id: def.id, value: '', cv };
                    return null;
                }).filter(Boolean).concat(customProps.filter((p) => !p.id));
                ordered.forEach((pr) => {
                    const def = schemaDefs.find((x) => x.id === pr.id);
                    const type = def ? def.type : pr.type;
                    const name = def ? def.name : pr.name;
                    const cv = pr.cv || computed[pr.id] || {};
                    if (COMPUTED_TYPES.includes(type)) { row(name, cv.err ? computedErr(cv) : (cv.t || EMPTY), null); return; }
                    if (type === 'relation') {
                        const box = document.createElement('div'); box.className = 'cal-rel-chips';
                        (cv.ids || []).forEach((id, i) => { const a = document.createElement('a'); a.className = 'cal-rel-chip'; a.href = '/calendar/events/' + id; a.textContent = (cv.titles || [])[i] || id; box.appendChild(a); });
                        if (cv.hidden) box.appendChild(mutedSpan('🔒 ' + (modal.dataset.lPrivateN || '%s').replace('%s', String(cv.hidden))));
                        if (!box.children.length) box.appendChild(mutedSpan(EMPTY));
                        row(name, box, 'props'); return;
                    }
                    if (type === 'status') {
                        const o = def && (def.options || []).find((x) => x.label === pr.value);
                        row(name, pr.value ? pill(pr.value, o ? o.color : 'o8') : EMPTY, 'props'); return;
                    }
                    if (type === 'users') {
                        const box = document.createElement('div'); box.style.cssText = 'display:flex; gap:4px; flex-wrap:wrap;';
                        parseMulti(pr.value).forEach((id) => box.appendChild(pill(optionLabels(document.getElementById('calAllUsers') || selects.users, id).join('') || id, '', true)));
                        row(name, box.children.length ? box : EMPTY, 'props'); return;
                    }
                    if (type === 'files') {
                        let refs = []; try { refs = JSON.parse(pr.value || '[]') || []; } catch (_) { refs = []; }
                        const box = document.createElement('div'); box.className = 'cal-prop-filebox'; box.setAttribute('data-file-group', '');
                        refs.forEach((f) => box.appendChild(fileTile(d.id, f))); // 읽기: 미리보기 줄 (AY5)
                        row(name, refs.length ? box : EMPTY, 'props'); return;
                    }
                    if (type === 'select' || type === 'multiselect') {
                        const box = document.createElement('div');
                        box.style.cssText = 'display:flex; gap:4px; flex-wrap:wrap;';
                        (type === 'multiselect' ? parseMulti(pr.value) : [pr.value]).forEach((v) => {
                            const o = def && (def.options || []).find((x) => x.label === v);
                            box.appendChild(pill(v, o ? o.color : 'o8'));
                        });
                        row(name, box, 'props');
                        return;
                    }
                    let val = pr.value || '';
                    if (type === 'check') val = pr.value === '1' ? '✓' : '–';
                    if (type === 'user') val = optionLabels(document.getElementById('calAllUsers') || selects.users, pr.value).join('') || pr.value;
                    if (type === 'url' && pr.value) {
                        const a = document.createElement('a');
                        a.href = pr.value; a.target = '_blank'; a.rel = 'noopener'; a.textContent = pr.value; a.style.color = 'inherit';
                        row(name, a, 'props');
                    } else {
                        row(name, val, 'props');
                    }
                });
            },
            memo: () => {
                const m = document.createElement('div');
                m.className = 'cal-read-memo';
                if (d.memo) m.textContent = d.memo;
                else m.appendChild(mutedSpan(EMPTY));
                row(formLabel('memo', L.lMemo), m, 'memo');
            },
        };
        formFieldOrder().forEach((f) => { if (renderers[f]) renderers[f](); });
        paintReadLinks();
        if (d.owner) row(L.lOwner, d.owner, null);
        const del = document.getElementById('calReadDelete');
        if (del) del.style.display = d.isowner === '1' ? 'inline-flex' : 'none';
        // 프로젝트 소속이면 헤더에 프로젝트 링크
        const projLink = document.getElementById('calPeekProject');
        if (projLink) {
            const o = d.calid && calSel ? Array.from(calSel.options).find((op) => op.value === d.calid) : null;
            const onHub = window.location.pathname.indexOf('/calendar/projects/') === 0;
            if (o && !onHub) {
                projLink.textContent = '↗ ' + o.textContent.trim();
                projLink.href = '/calendar/projects/' + encodeURIComponent(d.calid) + '?event=' + encodeURIComponent(d.id);
                projLink.hidden = false;
            } else {
                projLink.hidden = true;
            }
        }
    }
    // field: 편집으로 넘어가며 포커스할 항목 (title | start | end | time | calendar | memo | share | reminder | props)
    function enterEdit(field) {
        if (!currentChip || mode !== 'read') return;
        mode = 'edit';
        showPane(false);
        modalTitle.textContent = modalTitle.dataset.editLabel || modalTitle.textContent;
        window.history.replaceState({ calEvent: currentChip.dataset.id }, '', eventURL(currentChip.dataset.id, true));
        setDirty(false);
        const readEditBtn = document.getElementById('calReadEdit');
        if (readEditBtn) readEditBtn.hidden = true;
        const focusMap = {
            start: () => startBtn.click(),
            end: () => endBtn.click(),
            time: () => document.getElementById('calSegTimed').focus(),
            calendar: () => { const b = calSel && calSel.parentNode.querySelector('.cal-sel-btn'); if (b) b.click(); },
            memo: () => document.getElementById('calMemo').focus(),
            share: () => shareAll.closest('.cal-sel').querySelector('button').focus(),
            reminder: () => { const r = document.getElementById('calReminder'); const b = r && r.parentNode.querySelector('.cal-sel-btn'); if (b) b.click(); },
            repeat: () => { const chg = document.getElementById('calRepeatChange'); const sel = document.getElementById('calRepeat'); if (chg && !chg.hidden) chg.click(); else if (sel) sel.closest('.cal-sel').querySelector('button').click(); },
            props: () => { const first = document.querySelector('#calProps input'); if (first) first.focus(); else document.getElementById('calPropsAdd').click(); },
        };
        setTimeout(() => { (focusMap[field] || (() => { titleEl.focus(); titleEl.select(); }))(); }, 30);
    }
    const readEdit = document.getElementById('calReadEdit');
    if (readEdit) readEdit.addEventListener('click', () => enterEdit('title'));
    if (readPane) {
        readPane.addEventListener('click', (e) => {
            if (e.target.closest('a, button')) return;
            if (e.target.closest('#calReadTitle')) { enterEdit('title'); return; }
            const r = e.target.closest('.cal-prop-editable');
            if (r) enterEdit(r.dataset.field);
        });
    }
    const readDel = document.getElementById('calReadDelete');
    if (readDel) readDel.addEventListener('click', () => window.deleteCalendarEvent());
    const readDup = document.getElementById('calReadDup');
    if (readDup) readDup.addEventListener('click', () => { mode = 'edit'; showPane(false); dupBtn.click(); });

    // 취소·Esc·배경 클릭: 기존 일정을 고치던 중이면 닫지 않고 원래 값의 읽기 모드로
    // 돌아간다 (2026-09-17 #3) — 새 일정 작성만 닫힌다. 변경이 있으면 먼저 묻는다.
    window.cancelCalendarEdit = function () {
        if (mode !== 'edit' || !currentChip) {
            window.closeCalendarModal();
            return;
        }
        confirmDiscard().then((ok) => {
            if (!ok) return;
            dirty = false;
            setSaveState(null);
            const chip = findEventChip(currentChip.dataset.id) || currentChip;
            window.openCalendarEvent(chip, { noPush: true, replaceURL: true });
        });
    };

    // ── 할 일 체크: 읽기 모드에서 바로 완료/미완료 (패치 + 실행 취소 토스트) ──
    const readDoneCb = document.getElementById('calReadDone');
    if (readDoneCb) {
        readDoneCb.addEventListener('change', () => {
            if (!currentChip) return;
            const id = currentChip.dataset.id;
            const was = currentChip.dataset.done === '1';
            const cmd = patchCommand(id, { done: was ? '1' : '0' }, { done: was ? '0' : '1' });
            const L = labels();
            commit(cmd, was ? L.notDone : L.done).then(() => {
                const chip = findEventChip(id);
                if (chip) { currentChip = chip; fillRead(chip); }
                loadReadHistory(id); // 수정 내역 목록 (완료 줄의 "언제 · 누가" 는 새로 그린 칩의 스탬프에서)
            }).catch(() => {
                readDoneCb.checked = was;
                showToast(L.failed, { error: true });
            });
        });
        // 완료 줄의 「완료 해제」 = 링을 푸는 것과 같다.
        const doneMeta = document.getElementById('calReadDoneMeta');
        if (doneMeta) {
            doneMeta.addEventListener('click', (e) => {
                if (!e.target.closest('#calReadUndone') || !readDoneCb.checked) return;
                readDoneCb.checked = false;
                readDoneCb.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }
    }
    // ── 숨기기 / 숨기기 해제: 패널을 닫고 토스트(실행 취소)로 알린다. 숨긴 일정은
    // 필터 판의 「숨긴 일정 보기」를 켜야 (흐리게) 보인다. ──
    const readHide = document.getElementById('calReadHide');
    if (readHide) {
        readHide.addEventListener('click', () => {
            if (!currentChip) return;
            const id = currentChip.dataset.id;
            const was = currentChip.dataset.hidden === '1';
            const cmd = patchCommand(id, { hidden: was ? '1' : '0' }, { hidden: was ? '0' : '1' });
            const L = labels();
            closeModalOnly();
            commit(cmd, was ? L.unhidden : L.hidden).catch(() => showToast(L.failed, { error: true }));
        });
    }
    // ── 수정 내역: 읽기 패널 맨 아래에 전부 (GET /calendar/events/:id/history) ──
    let historySeq = 0;
    function loadReadHistory(id) {
        const box = document.getElementById('calReadHistory');
        const list = document.getElementById('calReadHistoryList');
        const empty = document.getElementById('calReadHistoryEmpty');
        if (!box || !list || !id) return;
        const seq = ++historySeq;
        box.hidden = false;
        list.innerHTML = '';
        if (empty) { empty.hidden = false; empty.textContent = modal.dataset.lHistoryLoading || '불러오는 중…'; }
        fetch('/calendar/events/' + encodeURIComponent(id) + '/history', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : { items: [] }))
            .then((data) => {
                if (seq !== historySeq) return; // 그 사이 다른 일정을 열었다
                const items = Array.isArray(data.items) ? data.items : [];
                list.innerHTML = '';
                items.forEach((it) => list.appendChild(historyRow(it)));
                if (empty) { empty.hidden = items.length > 0; empty.textContent = modal.dataset.lHistoryEmpty || ''; }
                paintDoneMeta(items);
            })
            .catch(() => {
                if (seq !== historySeq || !empty) return;
                empty.hidden = false;
                empty.textContent = modal.dataset.lHistoryEmpty || '';
            });
    }
    // 완료 상태 줄 (#calReadDoneMeta): 일정이 완료면 "완료 · 언제 · 누가" + 「완료 해제」. 언제·누가는 행의
    // 완료 스탬프(data-done-at/-by, 마이그레이션 124)이고, 스탬프가 없는 옛 완료(124 이전)만 수정 내역에서
    // 가장 최근에 완료로 바뀐 항목(field=done, to=완료 라벨)을 찾는다 — 내역이 아직 없으면 "완료" 만.
    function paintDoneMeta(items) {
        const meta = document.getElementById('calReadDoneMeta');
        if (!meta) return;
        if (!currentChip || currentChip.dataset.done !== '1') { meta.hidden = true; meta.textContent = ''; return; }
        const lDone = modal.dataset.lDone || '완료';
        const stampAt = currentChip.dataset.doneAt || '';
        let best = null;
        if (stampAt) {
            best = { at: stampAt, actor: currentChip.dataset.doneBy || '' };
        } else {
            (items || []).forEach((it) => {
                const hit = Array.isArray(it.changes) && it.changes.some((ch) => ch.field === 'done' && ch.to === lDone);
                if (hit && (!best || String(it.at || '') > String(best.at || ''))) best = it;
            });
        }
        const parts = [lDone];
        if (best) { if (best.at) parts.push(best.at); if (best.actor) parts.push(best.actor); }
        meta.innerHTML = '';
        meta.appendChild(document.createTextNode(parts.join(' · ')));
        const undo = document.createElement('button');
        undo.type = 'button';
        undo.className = 'cal-read-undone';
        undo.id = 'calReadUndone';
        undo.textContent = modal.dataset.lUndoDone || '완료 해제';
        meta.appendChild(undo);
        meta.hidden = false;
    }
    function historyRow(it) {
        const li = document.createElement('li');
        li.className = 'cal-history-row';
        li.dataset.kind = it.kind || '';
        const meta = document.createElement('span');
        meta.className = 'cal-history-meta';
        const at = document.createElement('time');
        at.className = 'cal-history-at';
        at.textContent = it.at || '';
        const actor = document.createElement('b');
        actor.className = 'cal-history-actor';
        actor.textContent = it.actor || '';
        const kind = document.createElement('span');
        kind.className = 'cal-history-kind';
        kind.textContent = it.kind_label || it.kind || '';
        meta.appendChild(at);
        meta.appendChild(actor);
        meta.appendChild(kind);
        if (it.scope_label) { // 반복 범위 편집(이후 모두/전체)이 남긴 행 (반복 스펙 §5.3)
            const scope = document.createElement('span');
            scope.className = 'cal-history-scope';
            scope.textContent = it.scope_label;
            meta.appendChild(scope);
        }
        li.appendChild(meta);
        const changes = Array.isArray(it.changes) ? it.changes : [];
        if (!changes.length) return li;
        const ul = document.createElement('ul');
        ul.className = 'cal-history-changes';
        changes.forEach((ch) => {
            const c = document.createElement('li');
            const f = document.createElement('span');
            f.className = 'cal-history-field';
            f.textContent = ch.label || ch.field || '';
            c.appendChild(f);
            if (ch.kind === 'added' || ch.kind === 'removed') {
                const s = document.createElement('span');
                s.className = ch.kind === 'added' ? 'cal-history-add' : 'cal-history-rm';
                s.textContent = (ch.kind === 'added' ? '+ ' : '− ') + (ch.kind === 'added' ? (ch.to || '') : (ch.from || ''));
                c.appendChild(s);
            } else if (ch.kind === 'color') {
                const a = document.createElement('i');
                a.className = 'cal-dot cal-' + (ch.from || 'c1');
                const arrow = document.createElement('span');
                arrow.className = 'cal-history-arrow';
                arrow.textContent = '→';
                const b = document.createElement('i');
                b.className = 'cal-dot cal-' + (ch.to || 'c1');
                c.appendChild(a);
                c.appendChild(arrow);
                c.appendChild(b);
            } else {
                const from = document.createElement('span');
                from.className = 'cal-history-from';
                from.textContent = ch.from || '';
                const arrow = document.createElement('span');
                arrow.className = 'cal-history-arrow';
                arrow.textContent = '→';
                const to = document.createElement('span');
                to.className = 'cal-history-to';
                to.textContent = ch.to || '';
                c.appendChild(from);
                c.appendChild(arrow);
                c.appendChild(to);
            }
            ul.appendChild(c);
        });
        li.appendChild(ul);
        return li;
    }

    // ── 복제: 현재 값을 그대로 든 채 '새 일정' 모드로 전환 (노션 duplicate) ──
    const dupBtn = document.getElementById('calDupBtn');
    dupBtn.addEventListener('click', () => {
        form.action = '/calendar/events';
        mode = 'create';
        showPane(false);
        setDirty(false);
        if (saveBtn) saveBtn.disabled = false;
        editBefore = null;
        sessionCmd = null;
        if (copyBtn) copyBtn.hidden = true;
        const readEditD = document.getElementById('calReadEdit');
        if (readEditD) readEditD.hidden = true;
        const projD = document.getElementById('calPeekProject');
        if (projD) projD.hidden = true;
        const openPageD = document.getElementById('calOpenPage');
        if (openPageD) openPageD.hidden = true;
        dropURLEvent();
        modalTitle.textContent = modalTitle.dataset.addLabel || modalTitle.textContent;
        document.getElementById('calDeleteBtn').style.display = 'none';
        dupBtn.style.display = 'none';
        document.getElementById('calOwnerLine').style.display = 'none';
        setSaveState(null);
        titleEl.focus();
        titleEl.select();
    });

    // ── Esc: 열린 것부터 차례로 닫는다 (데이트피커 → 공유 패널 → 모달) ──
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || modal.style.display === 'none' || !modal.style.display) return;
        if (isDatePickerOpen()) {
            closeDatePicker();
            return;
        }
        if (myPropAdd && !myPropAdd.hidden) {
            propertyCreate.close();
            return;
        }
        if (!panel.hidden) {
            panel.hidden = true;
            shareAdd.setAttribute('aria-expanded', 'false');
            return;
        }
        if (mode === 'edit') window.cancelCalendarEdit(); // 편집 중엔 읽기 모드로 (2026-09-17 #3)
        else window.closeCalendarModal();
    });

    // ── 캘린더 선택 시 그 프로젝트의 색을 기본으로 제안 ──
    const calSel = document.getElementById('calCalSel');
    // ── 상위 일정 (WBS): 선택된 캘린더의 일정 목록에서 부모를 고른다 ──
    const parentRow = document.getElementById('calParentRow');
    const parentSel = document.getElementById('calParentSel');
    let editingEventID = '';

    // ── 선행 일정 (FS 의존): 칩 + 추가 셀렉트, hidden input(dep_ids) 이 원본 ──
    const depsRow = document.getElementById('calDepsRow');
    const depsChips = document.getElementById('calDepsChips');
    const depsAdd = document.getElementById('calDepsAdd');
    const depsInputs = document.getElementById('calDepsInputs');
    let depIDs = [];
    let depTitles = {}; // id → 제목 (옵션 로드 후 채움)

    function syncDepInputs() {
        if (!depsInputs) return;
        depsInputs.innerHTML = '';
        depIDs.forEach((id) => {
            const inp = document.createElement('input');
            inp.type = 'hidden';
            inp.name = 'dep_ids';
            inp.value = id;
            depsInputs.appendChild(inp);
        });
    }
    function renderDepChips() {
        if (!depsChips) return;
        depsChips.innerHTML = '';
        depIDs.forEach((id) => {
            const chip = document.createElement('span');
            chip.className = 'cal-share-chip';
            const label = document.createElement('span');
            label.textContent = depTitles[id] || '…';
            chip.appendChild(label);
            const rm = document.createElement('button');
            rm.type = 'button';
            rm.textContent = '✕';
            rm.setAttribute('aria-label', 'remove');
            rm.addEventListener('click', () => {
                depIDs = depIDs.filter((x) => x !== id);
                syncDepInputs();
                renderDepChips();
                renderDepOptions();
                markDirty();
            });
            chip.appendChild(rm);
            depsChips.appendChild(chip);
        });
    }
    let depCandidates = [];
    function renderDepOptions() {
        if (!depsAdd) return;
        depsAdd.innerHTML = '';
        const first = document.createElement('option');
        first.value = '';
        first.textContent = depsAdd.dataset.addLabel || '＋ 선행 일정 추가…';
        depsAdd.appendChild(first);
        let count = 0;
        depCandidates.forEach((it) => {
            if (depIDs.indexOf(it.id) !== -1) return;
            const o = document.createElement('option');
            o.value = it.id;
            o.textContent = it.title + ' (' + it.start_date + ')';
            depsAdd.appendChild(o);
            count += 1;
        });
        depsAdd.disabled = count === 0;
        syncSelect(depsAdd);
    }
    if (depsAdd) {
        depsAdd.addEventListener('change', () => {
            if (!depsAdd.value) return;
            depIDs.push(depsAdd.value);
            depsAdd.value = '';
            syncDepInputs();
            renderDepChips();
            renderDepOptions();
            markDirty();
        });
    }
    // 옵션 로드 전 자동 저장이 나가도 값이 지워지지 않게 동기로 심는다.
    function seedDeps(csv) {
        depIDs = (csv || '').split(',').filter(Boolean);
        depTitles = {};
        depCandidates = [];
        syncDepInputs();
        renderDepChips();
        renderDepOptions();
    }

    // 옵션 로드 전 자동 저장이 나가도 부모가 지워지지 않게, 현재 값을
    // 동기로 심어 둔다.
    function seedParent(value) {
        if (!parentSel) return;
        parentSel.innerHTML = '';
        const o = document.createElement('option');
        o.value = value || '';
        o.textContent = value ? '…' : (parentSel.dataset.noneLabel || '없음');
        parentSel.appendChild(o);
        parentSel.value = value || '';
        syncSelect(parentSel);
    }

    function refreshParentOptions(calID, selfID, wantParent, wantDeps) {
        if (!parentRow || !parentSel) return;
        seedParent(wantParent);
        seedDeps(wantDeps);
        if (!calID) {
            parentRow.hidden = true;
            if (depsRow) depsRow.hidden = true;
            seedParent('');
            seedDeps('');
            return;
        }
        fetch('/calendar/calendars/' + encodeURIComponent(calID) + '/events', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        }).then((r) => (r.ok ? r.json() : [])).then((items) => {
            if (!Array.isArray(items)) items = [];
            // 자기 자신과 그 자손은 부모 후보에서 제외 (순환 방지)
            const childMap = {};
            items.forEach((it) => {
                if (it.parent_id) (childMap[it.parent_id] = childMap[it.parent_id] || []).push(it.id);
            });
            const excluded = new Set();
            if (selfID) {
                const stack = [selfID];
                while (stack.length) {
                    const id = stack.pop();
                    if (excluded.has(id)) continue;
                    excluded.add(id);
                    (childMap[id] || []).forEach((cid) => stack.push(cid));
                }
            }
            parentSel.innerHTML = '';
            const none = document.createElement('option');
            none.value = '';
            none.textContent = parentSel.dataset.noneLabel || '없음';
            parentSel.appendChild(none);
            let count = 0;
            items.forEach((it) => {
                if (excluded.has(it.id)) return;
                const o = document.createElement('option');
                o.value = it.id;
                o.textContent = it.title + ' (' + it.start_date + ')';
                parentSel.appendChild(o);
                count += 1;
            });
            parentSel.value = wantParent && !excluded.has(wantParent) ? wantParent : '';
            syncSelect(parentSel);
            parentRow.hidden = count === 0;
            // 선행 일정 후보: 자기 자신만 제외 (순환은 서버가 최종 차단)
            depTitles = {};
            depCandidates = [];
            items.forEach((it) => {
                depTitles[it.id] = it.title;
                if (it.id !== selfID) depCandidates.push(it);
            });
            depIDs = depIDs.filter((id) => depTitles[id]);
            syncDepInputs();
            renderDepChips();
            renderDepOptions();
            if (depsRow) depsRow.hidden = depCandidates.length === 0;
            paintReadLinks();
        }).catch(() => {
            parentRow.hidden = true;
            if (depsRow) depsRow.hidden = true;
        });
    }

    if (calSel) {
        calSel.addEventListener('change', () => {
            const opt = calSel.selectedOptions[0];
            if (opt && opt.dataset.color) {
                const radio = document.querySelector('#calColors input[value="' + opt.dataset.color + '"]');
                if (radio) radio.checked = true;
            }
            refreshParentOptions(calSel.value, editingEventID, '', depIDs.join(','));
        });
    }

    // ── 프로젝트 캘린더: 생성 모달 + 삭제 ──
    const projModal = document.getElementById('calProjModal');
    const projPicker = projModal ? makeSharePicker({
        users: 'calPShareUsers', depts: 'calPShareDepts', groups: 'calPShareGroups',
        chips: 'calPShareChips', empty: 'calPShareEmpty', panel: 'calPSharePanel',
        add: 'calPShareAdd', search: 'calPShareSearch', list: 'calPShareList',
    }, null) : null;

    window.closeCalProjModal = function () {
        const m = document.getElementById('calProjModal');
        if (!m) return;
        m.style.display = 'none';
        document.body.classList.remove('modal-scroll-lock');
    };
    // 프로젝트 정보(기간·상태·PM·설명·Redmine) 입력기
    const pStart = document.getElementById('calProjStart'), pStartBtn = document.getElementById('calProjStartBtn');
    const pEnd = document.getElementById('calProjEnd'), pEndBtn = document.getElementById('calProjEndBtn');
    function syncProjDates() {
        if (pStartBtn && pStart) syncDateLabel(pStartBtn, pStart, pickPh);
        if (pEndBtn && pEnd) syncDateLabel(pEndBtn, pEnd, pickPh);
    }
    if (pStartBtn && pEndBtn && pStart && pEnd) {
        const base = { weekdays: modal.dataset.weekdays, weekStart: modal.dataset.weekStart, placeholder: pickPh, clearLabel: modal.dataset.lTbd || '미정' };
        attachDatePicker(pStartBtn, pStart, Object.assign({}, base, { onSet: (v) => { if (v && pEnd.value && pEnd.value < v) pEnd.value = v; syncProjDates(); } }));
        attachDatePicker(pEndBtn, pEnd, Object.assign({}, base, { onSet: (v) => { if (v && pStart.value && v < pStart.value) pStart.value = v; syncProjDates(); } }));
        syncProjDates();
    }
    if (projModal) initInappSelects(projModal);
    function fillProjInfo(d) {
        const set = (id, v) => { const el = document.getElementById(id); if (el) { el.value = v || ''; if (el.tagName === 'SELECT') syncSelect(el); } };
        set('calProjStart', d.calStart); set('calProjEnd', d.calEnd);
        // 담당 PM 은 값을 넣기 전에 ⚠ 옵션을 맞춘다 — 목록에 없는 값을 넣으면
        // select 가 빈 값으로 떨어져 저장 때 담당자가 지워진다.
        syncMissingOptions(document.getElementById('calProjManager'), [d.calManager || ''], missingPeopleOf(d));
        set('calProjStatus', d.calStatus || 'active'); set('calProjManager', d.calManager);
        set('calProjDesc', d.calDesc); set('calProjExtUrl', d.calExtUrl); set('calProjExtRef', d.calExtRef);
        syncProjDates();
    }
    function openProjModal(editBtn) {
        const projForm = document.getElementById('calProjForm');
        const projTitle = document.getElementById('calProjTitle');
        const projSubmit = document.getElementById('calProjSubmit');
        projForm.reset();
        fillProjInfo(editBtn ? editBtn.dataset : {});
        document.getElementById('calProjCsrf').value = window.getCSRFToken ? window.getCSRFToken() : '';
        if (editBtn) {
            // 편집 모드: 버튼의 데이터로 프리필, 액션을 update 로.
            projForm.action = '/calendar/calendars/' + editBtn.dataset.calId + '/update';
            projTitle.textContent = projTitle.dataset.editLabel || projTitle.textContent;
            projSubmit.textContent = projSubmit.dataset.editLabel || projSubmit.textContent;
            document.getElementById('calProjName').value = editBtn.dataset.calName || '';
            const radio = projForm.querySelector('input[name="color"][value="' + (editBtn.dataset.calColor || 'c1') + '"]');
            if (radio) radio.checked = true;
            if (projPicker) {
                setMulti(projPicker.selects.users, editBtn.dataset.shareUsers, missingPeopleOf(editBtn.dataset));
                setMulti(projPicker.selects.depts, editBtn.dataset.shareDepts);
                setMulti(projPicker.selects.groups, editBtn.dataset.shareGroups);
            }
        } else {
            // 새 프로젝트: 앞서 연 편집 모달이 끼워 넣은 ⚠ 옵션을 치운다.
            if (projPicker) setMulti(projPicker.selects.users, '', {});
            projForm.action = '/calendar/calendars';
            projTitle.textContent = projTitle.dataset.addLabel || projTitle.textContent;
            projSubmit.textContent = projSubmit.dataset.addLabel || projSubmit.textContent;
        }
        if (projPicker) projPicker.reset();
        // 모달은 클릭 시점에 찾는다 — 경로가 바뀌는 제자리 교체(swapPage)는
        // 이 노드도 새 문서 것으로 갈아끼운다.
        document.getElementById('calProjModal').style.display = 'flex';
        document.body.classList.add('modal-scroll-lock');
        document.getElementById('calProjName').focus();
    }
    // ＋ 새 프로젝트 캘린더 · 편집 · 삭제 버튼은 필터 폼([data-spa-region="filters"])
    // 안에 있고, 그 영역은 SPA 새로고침(달 이동·필터 변경·월↔목록)마다 innerHTML
    // 로 통째로 다시 그려진다 — 노드마다 걸어 둔 리스너는 두 번째 조작부터
    // 사라진 노드에 남아 버튼이 조용히 죽었다. 그래서 문서에 위임한다.
    document.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        if (!document.getElementById('calProjModal')) return;
        if (e.target.closest('#calProjAdd')) { openProjModal(null); return; }
        const edit = e.target.closest('.cal-proj-edit');
        if (edit) { openProjModal(edit); return; }
        const del = e.target.closest('.cal-proj-del:not(.cal-proj-edit)');
        if (del) deleteProjCalendar(del);
    });
    function deleteProjCalendar(btn) {
        const doDelete = () => {
            const delForm = document.getElementById('calProjDeleteForm');
            if (!delForm) return;
            delForm.action = '/calendar/calendars/' + btn.dataset.calId + '/delete';
            document.getElementById('calProjDeleteCsrf').value = window.getCSRFToken ? window.getCSRFToken() : '';
            delForm.submit();
        };
        const msg = btn.dataset.confirmMsg || 'Delete this calendar?';
        if (window.appConfirm) {
            window.appConfirm({ message: msg, danger: true }).then((ok) => { if (ok) doDelete(); });
        } else if (window.confirm(msg)) {
            doDelete();
        }
    }
    if (projModal) {
        document.addEventListener('keydown', (e) => {
            const m = document.getElementById('calProjModal');
            if (e.key !== 'Escape' || !m || m.style.display !== 'flex') return;
            if (projPicker && !projPicker.panel.hidden) {
                projPicker.panel.hidden = true;
                projPicker.addBtn.setAttribute('aria-expanded', 'false');
                return;
            }
            window.closeCalProjModal();
        });
    }

    // ── 프로젝트 페이지에서 넘어온 "일정 추가" (?compose=<calID>) ──
    (function composeFromQuery() {
        const url = new URL(window.location.href);
        const calID = url.searchParams.get('compose');
        if (!calID) return;
        url.searchParams.delete('compose');
        window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
        setTimeout(() => {
            window.openCalendarCreate();
            const sel = document.getElementById('calCalSel');
            if (sel) {
                sel.value = calID;
                syncSelect(sel);
                sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }, 0);
    })();

    // 배경 클릭: 아직 아무것도 안 쓴 새 일정일 때만 닫는다. 기존 일정을 보는 중에는
    // 실수로 닫히지 않게 X·취소·Esc 로만 닫는다.
    const backdrop = document.getElementById('calPeekBackdrop');
    if (backdrop) {
        backdrop.addEventListener('click', () => {
            if (mode === 'read') return; // 읽는 중엔 X·Esc 로만
            if (mode === 'create' && titleEl.value.trim()) return; // 작성 중인 새 일정 보호
            if (mode === 'edit') { window.cancelCalendarEdit(); return; } // 편집 중엔 읽기 모드로
            window.closeCalendarModal();
        });
    }
    // "페이지로 열기": 일정 전용 페이지 (/calendar/events/<id>)
    const openPageBtn = document.getElementById('calOpenPage');
    if (openPageBtn) {
        openPageBtn.addEventListener('click', () => {
            if (editingEventID) window.location.href = '/calendar/events/' + encodeURIComponent(editingEventID);
        });
    }
    // 뒤로/앞으로: 주소의 ?event= 에 맞춰 패널을 열고 닫는다.
    window.addEventListener('popstate', () => {
        const id = currentURLEvent();
        if (id) {
            const chip = findEventChip(id);
            if (chip) window.openCalendarEvent(chip, { noPush: true, edit: currentURLEdit() });
        } else if (modal.style.display === 'flex') {
            clearTimeout(saveTimer);
            modal.style.display = 'none';
            document.body.classList.remove('modal-scroll-lock');
        }
    });
    // 딥링크(/calendar/events/<id> 또는 ?event=): 서버가 접근 확인 후 넘긴 id 를 연다.
    (function openFromURL() {
        const id = modal.dataset.openEvent || currentURLEvent();
        if (!id) return;
        const chip = findEventChip(id);
        const edit = modal.dataset.openEdit === '1' || currentURLEdit();
        if (chip) setTimeout(() => window.openCalendarEvent(chip, { noPush: true, edit }), 0);
    })();

    window.deleteCalendarEvent = function () {
        const btn = document.getElementById('calDeleteBtn');
        const id = btn.dataset.eventId;
        if (!id) return;
        const L = labels();
        // 반복 인스턴스: 범위를 묻는다 (반복 스펙 §6.2). 「이 일정만」은 예전 길(실행 취소 가능).
        const isInstance = !!(currentChip && currentChip.dataset.id === id && currentChip.dataset.series);
        const scopeP = isInstance ? askSeriesScope('delete') : Promise.resolve('this');
        scopeP.then((scope) => {
            if (scope === null) return null;
            btn.disabled = true;
            const body = scope === 'this' ? {} : { series_scope: scope };
            return api.remove(id, body).then((r) => {
                savedAny = false;
                closeModalOnly();
                if (scope === 'this') return record(deleteCommand(id), L.deleted);
                seriesCache = {};
                showToast(fmtN(RL.msgDeleted || '일정 %d개를 지웠습니다', (r && r.affected) || 1));
                return refreshOrReload();
            }).catch(() => {
                showToast(L.failed, { error: true });
            }).finally(() => {
                btn.disabled = false;
            });
        });
    };
}


// ── 날짜 시트: 하루 일정 목록 + 추가 (Android 캘린더 패턴) ──
window.openCalendarDay = function (date) {
    if (!date) return;
    // 데스크톱: 빈 영역 클릭은 곧바로 그 날짜의 일정 추가 (노션 방식).
    // 바 자체가 클릭 가능하니 그날 목록은 모바일에서만 의미가 있다.
    if (!isPhone()) {
        if (window.openCalendarQuickAdd) window.openCalendarQuickAdd(date, date);
        else if (window.openCalendarCreate) window.openCalendarCreate(date);
        return;
    }
    // 모바일: 시트 대신 그리드 아래 그날 목록을 그 날짜로 (spec 2026-09-23 §4.1, calendar-agenda.js).
    if (window.selectCalendarDay) window.selectCalendarDay(date);
};

document.addEventListener('keydown', function (e) {
    // ←/→ 로 월 이동 — 모달이 닫혀 있고 입력 중이 아닐 때만.
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName || '')) return;
    var m = document.getElementById('calendarModal');
    if (m && m.style.display === 'flex') return;
    var nav = document.getElementById(e.key === 'ArrowLeft' ? 'calNavPrev' : 'calNavNext');
    if (nav) nav.click();
});
